import { QueryClient } from "@tanstack/react-query"
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios"
import applyCaseMiddleware from "axios-case-converter"
import axiosRetry, { isNetworkError } from "axios-retry"

import {
  SESSION_EXPIRED_CODE,
  apiRetryDelay,
  isRetryableApiError,
  maxApiRetryAttempts,
} from "@/api/errors"
import { postErrorV2ToParentWindow } from "@/api/importerEvents"
import { SECOND_IN_MS } from "@/constants"
import { fastJsonStringify } from "@/util/json"
import { monitorServerReachability } from "@/util/networkMonitor"
import { logEvent } from "@/util/telemetry"
import { type JsonValue, isDate } from "@/util/types"

export const TRANSACTION_ID_HEADER = "x-oneschema-transaction-id"
export const ONESCHEMA_UNAVAILABLE_EVENT = "oneschema:unavailable"
export const ONESCHEMA_SESSION_EXPIRED_EVENT = "oneschema:session-expired"

/**
 * Extract the chain-wide transaction id from a response (or error response).
 * Returns `undefined` when the header is absent.
 */
export function extractTransactionId(
  response: AxiosResponse | undefined,
): string | undefined {
  return response?.headers?.[TRANSACTION_ID_HEADER] ?? undefined
}

function findAxiosError(error: unknown): AxiosError | undefined {
  if (axios.isAxiosError(error)) {
    return error
  }
  if (typeof error !== "object" || error === null || !("cause" in error)) {
    return undefined
  }
  const cause = (error as { cause?: unknown }).cause
  if (cause === undefined || cause === error) {
    return undefined
  }
  return findAxiosError(cause)
}

/**
 * Extract the transaction id from an error thrown by an axios call.
 * Works with raw `AxiosError`s and Error wrappers whose `cause` is an AxiosError.
 */
export function extractErrorTransactionId(error: unknown): string | undefined {
  const axiosError = findAxiosError(error)
  if (axiosError !== undefined) {
    return extractTransactionId(axiosError.response)
  }
  return undefined
}

export type ConfigModalErrorCategory =
  | "network"
  | "timeout"
  | "auth_expired"
  | "resource_unavailable"
  | "unavailable"
  | "provider"
  | "transient"
  | "internal"
  | "unknown"

const CONFIG_MODAL_ERROR_GUIDANCE: Record<ConfigModalErrorCategory, string> = {
  network: "Unable to reach the server \u2014 if this persists, please contact support.",
  timeout: "The server is temporarily unavailable. Please try again in a moment.",
  auth_expired: "Your session has expired. Please refresh the page and try again.",
  resource_unavailable:
    "The requested resource is no longer accessible or you don't have permission. Try again or contact support.",
  unavailable: "OneSchema is under maintenance. Please try again in a few minutes.",
  provider: "Third-party service error \u2014 retry may help.",
  transient: "Temporarily unavailable.",
  internal:
    "OneSchema internal error \u2014 please include the transaction ID when contacting support.",
  unknown: "",
}

/**
 * Classify an error from a config modal API call into a user-facing category
 * with actionable guidance. Returns `undefined` for non-axios errors
 * (e.g. client-side validation).
 */
export function classifyConfigModalError(
  error: unknown,
): { category: ConfigModalErrorCategory; guidance: string } | undefined {
  const axiosError = findAxiosError(error)
  if (axiosError === undefined) {
    return undefined
  }

  if (axiosError.code === "ERR_CANCELED") {
    return undefined
  }

  const status = axiosError.response?.status

  if (status == null) {
    if (axiosError.code === "ECONNABORTED") {
      return { category: "timeout", guidance: CONFIG_MODAL_ERROR_GUIDANCE.timeout }
    }
    return { category: "network", guidance: CONFIG_MODAL_ERROR_GUIDANCE.network }
  }
  if (status === 401) {
    return {
      category: "auth_expired",
      guidance: CONFIG_MODAL_ERROR_GUIDANCE.auth_expired,
    }
  }
  if (status === 403 || status === 404) {
    return {
      category: "resource_unavailable",
      guidance: CONFIG_MODAL_ERROR_GUIDANCE.resource_unavailable,
    }
  }
  if (status === 502) {
    return { category: "provider", guidance: CONFIG_MODAL_ERROR_GUIDANCE.provider }
  }
  if (isUnavailableModeResponse(axiosError, status)) {
    return {
      category: "unavailable",
      guidance: CONFIG_MODAL_ERROR_GUIDANCE.unavailable,
    }
  }
  if (status === 503 || status === 504) {
    return { category: "transient", guidance: CONFIG_MODAL_ERROR_GUIDANCE.transient }
  }
  if (status >= 500) {
    return { category: "internal", guidance: CONFIG_MODAL_ERROR_GUIDANCE.internal }
  }
  return { category: "unknown", guidance: CONFIG_MODAL_ERROR_GUIDANCE.unknown }
}

/**
 * Extract the human-readable `{ error: "..." }` string OneSchema
 * controllers attach to 422 `BusinessLogicError` responses. Returns
 * `undefined` for non-422 errors or response shapes that don't match —
 * callers should fall back to `classifyConfigModalError` for those.
 *
 * Centralizes the transport-internals access (`error.response`) so
 * component code can stay free of axios specifics, per the
 * `frontend/.rules/frontend-typescript.md` API-error-containment rule.
 */
export function extractBusinessLogicErrorMessage(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) {
    return undefined
  }
  if (error.response?.status !== 422) {
    return undefined
  }
  const data: unknown = error.response.data
  if (
    data &&
    typeof data === "object" &&
    "error" in data &&
    typeof data.error === "string"
  ) {
    return data.error
  }
  return undefined
}

const DEFAULT_CONFIG: AxiosRequestConfig = {
  headers: { "Content-Type": "application/json" },
  timeout: 60 * SECOND_IN_MS,
}

const AXIOS_RETRY_CONFIG = {
  retries: 5,
  retryCondition: isNetworkError,
  retryDelay: (retryNumber: number) => axiosRetry.exponentialDelay(retryNumber),
  onRetry: function onRetry(
    retryCount: number,
    error: AxiosError,
    requestConfig: AxiosRequestConfig,
  ) {
    logEvent("info", "Axios retry attempt", {
      extra: {
        error,
        retryCount,
        "requestConfig.url": requestConfig.url,
        "requestConfig.method": requestConfig.method,
        "requestConfig.axiosRetry": requestConfig["axios-retry"],
      },
    })
  },
}

function isUnavailableModeResponse(
  error: AxiosError | Error,
  status: HttpResponseStatus | undefined,
): boolean {
  return (
    axios.isAxiosError(error) &&
    status === 503 &&
    serverErrorCode(error) === "unavailable"
  )
}

function serverErrorCode(error: AxiosError): string | undefined {
  const data = error.response?.data
  if (!data || typeof data !== "object") {
    return undefined
  }
  if ("code" in data && typeof data.code === "string") {
    return data.code
  }
  if ("detail" in data && data.detail && typeof data.detail === "object") {
    const detail = data.detail
    if ("code" in detail && typeof detail.code === "string") {
      return detail.code
    }
  }
  return undefined
}

// == Case-Converting Client ==

/**
 * A client that applies snake_case-to-camelCase conversions automatically.
 */
export const caseConverterClient = axios.create(DEFAULT_CONFIG)
applyCaseMiddleware(caseConverterClient, {
  ignoreHeaders: true,
})

// == Case-Preserving Client ==

/**
 * A client that doesn't apply automatic snake_case-to-camelCase conversions.
 *
 * Use this if you need the original casing for whatever reason. When using
 * this, the request params must be snake_case AND the returned data will be in
 * snake_case.
 */
export const originalCaseClient = axios.create(DEFAULT_CONFIG)

// == Event loggers ==

for (const client of [caseConverterClient, originalCaseClient]) {
  client.interceptors.request.use(null, onRequestError)
  client.interceptors.response.use(on2xxResponse, onOtherResponse)
  axiosRetry(client, AXIOS_RETRY_CONFIG)
}

function onRequestError(error: AxiosError | Error) {
  logEvent("error", `http-req-error: ${error.message ?? String(error)}`, {
    exception: error,
  })

  return Promise.reject(error)
}

const EXPECTED_2XX_STATUS_CODES = new Set([
  200, // OK
  204, // No Content (e.g. fire-and-forget telemetry beacons)
])

function on2xxResponse(response: AxiosResponse) {
  if (!EXPECTED_2XX_STATUS_CODES.has(response.status)) {
    const {
      request,
      status,
      statusText,
      config: { method, url },
    } = response
    const transactionId = extractTransactionId(response)
    logEvent("warn", `http-unexp-2xx: ${status} ${statusText} for ${method} ${url}`, {
      extra: {
        "request.readystate": request?.readyState,
        "request.responseType": request?.responseType,
        "request.responseURL": request?.responseURL,
      },
      tags: {
        ...(transactionId ? { transaction_id: transactionId } : {}),
      },
    })
  }

  return response
}

const KNOWN_SERVER_RESPONSE_STATUS_CODES: Set<number | "UNK"> = new Set([
  400, // Bad Request
  401, // Unauthorized
  403, // Forbidden
  422, // Unprocessable Content
])

const UNKNOWN_HTTP_VALUE = "UNK"
type HttpResponseStatus = number | typeof UNKNOWN_HTTP_VALUE

function normalizeHttpRoute(url: string | undefined): string {
  if (!url) {
    return UNKNOWN_HTTP_VALUE
  }

  try {
    const { pathname } = new URL(url, "https://oneschema.invalid")
    const normalizedPath = pathname
      .split("/")
      .map((segment) => (isDynamicPathSegment(segment) ? ":id" : segment))
      .join("/")

    return normalizedPath || "/"
  } catch (_err) {
    return url.split("?")[0] || UNKNOWN_HTTP_VALUE
  }
}

function isDynamicPathSegment(segment: string): boolean {
  return (
    /^\d+$/.test(segment) ||
    /^[0-9a-f]{16,}$/i.test(segment) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
  )
}

export function getHttpErrorLoggingContext(error: AxiosError | Error) {
  const {
    response,
    config: { method = undefined, url = undefined } = {},
    code,
  } = (error || {}) as AxiosError
  const responseStatus: HttpResponseStatus = response?.status ?? UNKNOWN_HTTP_VALUE
  const errorCode = code ?? UNKNOWN_HTTP_VALUE
  const requestMethod = method?.toLowerCase() ?? UNKNOWN_HTTP_VALUE
  const requestRoute = normalizeHttpRoute(url)

  return {
    response,
    responseStatus,
    tags: {
      "response.status": responseStatus,
      "error.code": errorCode,
      "request.method": requestMethod,
      "request.route": requestRoute,
    },
    fingerprint: [
      "http-error",
      requestMethod,
      requestRoute,
      String(responseStatus),
      errorCode,
    ],
  }
}

function onOtherResponse(error: AxiosError | Error) {
  const { config: { method = undefined, url = undefined } = {} } = (error ||
    {}) as AxiosError
  const { response, responseStatus, tags, fingerprint } =
    getHttpErrorLoggingContext(error)
  const transactionId = extractTransactionId(response)
  const isUnavailableMode = isUnavailableModeResponse(error, responseStatus)
  const isSessionExpired =
    axios.isAxiosError(error) &&
    responseStatus === 401 &&
    serverErrorCode(error) === SESSION_EXPIRED_CODE
  if (isUnavailableMode) {
    void queryClient.cancelQueries()
    queryClient.clear()
    window.dispatchEvent(new Event(ONESCHEMA_UNAVAILABLE_EVENT))
    logEvent("warn", `http-error: ${method} ${url}: ${error.message ?? String(error)}`, {
      exception: error,
      tags: {
        ...tags,
        ...(transactionId ? { transaction_id: transactionId } : {}),
      },
      fingerprint,
      shouldCapture: false,
    })
    // Intentionally never settles: the unavailable event takes over the UI.
    return new Promise(() => {})
  }
  if (isSessionExpired) {
    window.dispatchEvent(new Event(ONESCHEMA_SESSION_EXPIRED_EVENT))
  }
  const isKnownServerResponse = KNOWN_SERVER_RESPONSE_STATUS_CODES.has(responseStatus)

  if (!isKnownServerResponse) {
    void monitorServerReachability(`http-${responseStatus}`)
  }

  postErrorV2ToParentWindow("HTTP Error")

  logEvent("warn", `http-error: ${method} ${url}: ${error.message ?? String(error)}`, {
    exception: error,
    tags: {
      ...tags,
      ...(transactionId ? { transaction_id: transactionId } : {}),
    },
    fingerprint,
    shouldCapture: isKnownServerResponse ? false : "auto",
  })

  return Promise.reject(error)
}

// == Other helpers ==

// Used when embedding to configure the auth token header
// to be sent with every request.
export function setEmbedHeaderAuthToken(authToken: string) {
  caseConverterClient.defaults.headers.common["X-One-Schema-Embed-Auth-Token"] = authToken
  originalCaseClient.defaults.headers.common["X-One-Schema-Embed-Auth-Token"] = authToken
}

export type Response<T> = Promise<AxiosResponse<T>>

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry(failureCount, error) {
        return isRetryableApiError(error) && failureCount < maxApiRetryAttempts()
      },
      retryDelay(retryCount) {
        return apiRetryDelay(retryCount)
      },
    },
  },
})

type ParamValue = JsonValue | Date

/**
 * Encodes a single query param
 */
const encodeAxiosParam = (value: ParamValue) => {
  let encValue: string | number | boolean
  if (isDate(value)) {
    // Date
    encValue = value.toISOString()
  } else if (typeof value === "object") {
    // Object, Array, null
    encValue = fastJsonStringify(value)
  } else {
    // string, number, boolean
    encValue = value
  }
  return encodeURIComponent(encValue)
}

/**
 * Serializes params to a query string like axios pre-v1
 * This is a known issue with axios v1.
 * This code is borrowed from https://github.com/axios/axios/issues/5630#issuecomment-1859776798
 *
 * ```
 * {
 *   object: { a: 1 },                        // object=%7B%22a%22:1%7D
 *   arrayOfArray: [[1, 2, ","], [2]],        // arrayOfArray[]=[1,2,%22,%22]&arrayOfArray[]=[2]
 *   arrayOfNullUndefined: [null, undefined], // arrayOfNullUndefined[]=null&arrayOfNullUndefined[]=undefined
 *   arrayOfObject: [{ id: 1 }, { id: 12 }],  // arrayOfObject[]=%7B%22id%22:1%7D&arrayOfObject[]=%7B%22id%22:12%7D
 *   arrayOfString: ["a", "b", "c"],          // arrayOfString[]=a&arrayOfString[]=b&arrayOfString[]=c
 *   undefinable: undefined,                  // X
 *   nullable: null,                          // X
 *   emptyArray: [],                          // X
 *   emptyString: "",                         // emptyString=
 *   emptyObject: {},                         // emptyObject=%7B%7D
 *   date: new Date("1970-01-01"),            // date=1970-01-01T00:00:00.000Z
 * }
 * ```
 *
 * FIXME: Remove this in favor of updating the API endpoints
 */
export const paramsSerializerV0 = (params: { [key: string]: ParamValue }) =>
  Object.entries(params)
    .filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        (Array.isArray(value) ? value.length > 0 : true),
    )
    .map(([key, value]: [string, ParamValue]) => {
      if (Array.isArray(value)) {
        return value.map((v) => `${key}[]=${encodeAxiosParam(v)}`).join("&")
      }
      return `${key}=${encodeAxiosParam(value)}`
    })
    .join("&")
